import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SecurityContext,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ProjectModel } from '../../../../../../core/models/project/project.model';
import { ShiveGridWrapperComponent } from '../../../../../../shared/components/shive-controls/shive-grid/shive-grid-wrapper.component';
import { UserOperation } from 'src/app/core/enums/user-operation';
import {
  BudgetOptions,
  ExpenditureOptions,
} from '../../../../../../core/constants-repository/project-calculation.constants';
import { faCircleMinus, faListCheck, faPen } from '@fortawesome/pro-light-svg-icons';
import { SubSink } from 'subsink';
import { ProjectTaskDialogComponent } from '../../dialogs/project-task-dialog/project-task-dialog.component';
import { ShiveDialogService } from '../../../../../../core/services/controls/shive-dialog.service';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { ProjectDataService } from '../../../state-and-data-handling/project-data.service';
import { ProjectTaskService } from '../../services/project-task.service';
import { filter, take } from 'rxjs/operators';
import { ProjectExpenditureBudgetTransformerService } from '../project-calculation-form/services/project-expenditure-budget-transformer.service';
import { ProjectTaskGridService } from './project-task-grid.service';
import { cloneDeep } from 'lodash-es';
import { ProjectTaskGridRow } from '../../../../shared/project-detail-types';
import { TimeFormatType } from '../../../../../../core/pipes/time-format.pipe';
import { ExpenditureRateEntry } from '../../../../../../core/models/project/project-expenditure-entry.model';

@Component({
  selector: 'app-project-task-grid',
  templateUrl: './project-task-grid.component.html',
  standalone: false,
})
export class ProjectTaskGridComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() set selectedExpenditureRateOption(v: ExpenditureOptions) {
    this._selectedExpenditureRateOption.set(v);
  }
  @Input() set selectedBudgetOption(v: BudgetOptions) {
    this._selectedBudgetOption.set(v);
  }

  @Input() set removedAssigneeId(assigneeId: number) {
    this.projectTaskService.setUnassignedUserForRemovedAssignee(this.gridData, assigneeId);
  }

  // Derived UI state (computed signals)
  readonly showHourlyExpenditureRateFormField = computed(
    () => this._selectedExpenditureRateOption() === ExpenditureOptions.HourlyRatePerTask,
  );

  readonly showBudgetMonetaryPerTaskFormField = computed(
    () => this._selectedBudgetOption() === BudgetOptions.MonetaryPerTask,
  );

  readonly showBudgetHoursPerTaskFormField = computed(
    () => this._selectedBudgetOption() === BudgetOptions.HoursPerTask,
  );

  // Misc
  @ViewChild('gridWrapperComponent') gridWrapperComponent: ShiveGridWrapperComponent;

  // Constants
  public readonly UserOperation = UserOperation;
  public readonly SecurityContext = SecurityContext;
  public readonly faListCheck = faListCheck;
  public readonly faPenToSquare = faPenToSquare;
  public readonly TimeFormatType = TimeFormatType;

  // DI
  public readonly projectTaskGridService = inject(ProjectTaskGridService);
  private readonly shiveDialogService = inject(ShiveDialogService);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly projectTaskService = inject(ProjectTaskService);
  private readonly projectExpenditureBudgetTransformer = inject(
    ProjectExpenditureBudgetTransformerService,
  );

  // Inputs -> internal signals
  private readonly _selectedExpenditureRateOption = signal<ExpenditureOptions | null>(null);
  private readonly _selectedBudgetOption = signal<BudgetOptions | null>(null);

  private readonly subs = new SubSink();

  // Getter / Setter
  public get gridData(): ProjectTaskGridRow[] {
    return this.projectTaskGridService.gridData();
  }

  private get project(): ProjectModel {
    return this.projectDataService.getProjectClone();
  }

  ngOnInit(): void {
    const project = this.project;

    // Initialize computed signals
    if (!this._selectedExpenditureRateOption()) {
      this._selectedExpenditureRateOption.set(project.expenditureType);
    }
    if (!this._selectedBudgetOption()) {
      this._selectedBudgetOption.set(project.budgetType);
    }

    // Seed grid with existing tasks
    this.projectDataService.getTasks(project, null, false).forEach((task) => {
      this.gridData.push({
        task,
        budgetSeconds: 0,
        budgetMonetary: 0,
        expenditureRateHistory: [],
      });
    });

    // Apply expenditure/budget data to the grid
    this.projectExpenditureBudgetTransformer.transformExpenditureAndBudgetEntriesToTaskGridData(
      this.gridData,
      project,
    );
  }

  ngAfterViewInit(): void {
    this.initContextMenuCallbacks();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If parent initializes inputs, skip the mutation work below
    if (
      changes.selectedExpenditureRateOption?.firstChange ||
      changes.selectedBudgetOption?.firstChange
    ) {
      return;
    }

    const project = this.project;

    if (
      changes.selectedExpenditureRateOption &&
      changes.selectedExpenditureRateOption.currentValue === ExpenditureOptions.HourlyRatePerTask
    ) {
      this.projectTaskService.resetExpenditure(this.gridData);
      this.projectTaskService.syncProjectModel(this.gridData, project);
    }

    if (changes.selectedBudgetOption) {
      const value = changes.selectedBudgetOption.currentValue as BudgetOptions;
      if (value === BudgetOptions.HoursPerTask || value === BudgetOptions.MonetaryPerTask) {
        this.projectTaskService.resetBudget(this.gridData);
        this.projectTaskService.syncProjectModel(this.gridData, project);
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // Apply result of updated history rate entries edited in grid.
  public readonly makeApplyResult =
    (row: ProjectTaskGridRow) => (entries: ExpenditureRateEntry[]) => {
      this.projectTaskService.setHistoryForTask(this.gridData, row.task.id, entries);
      this.projectTaskService.syncProjectModel(this.gridData, this.project);
      this.projectDataService.userOperationConducted$.next(true);
    };

  public openTaskDialog(userOperation: UserOperation, taskGridRow?: ProjectTaskGridRow): void {
    let initialRow = cloneDeep(taskGridRow);
    let title = 'Aufgabe bearbeiten';

    if (userOperation === UserOperation.Create) {
      initialRow = this.projectTaskGridService.createEmptyTaskGridDRow();
      title = 'Aufgabe hinzufügen';
    }

    const ref = this.shiveDialogService.open({
      content: ProjectTaskDialogComponent,
      width: 572,
      title,
    });

    const dialogCmp = ref.content.instance as ProjectTaskDialogComponent;

    // Set dialog inputs (they are signals in the dialog)
    dialogCmp.taskGridRow.set(initialRow);
    dialogCmp.userOperation.set(userOperation);
    dialogCmp.rateAndBudget.set(this.getActiveColumnValue(initialRow));
    dialogCmp.showHourlyExpenditureRateFormField.set(this.showHourlyExpenditureRateFormField());
    dialogCmp.showBudgetMonetaryPerTaskFormField.set(this.showBudgetMonetaryPerTaskFormField());
    dialogCmp.showBudgetHoursPerTaskFormField.set(this.showBudgetHoursPerTaskFormField());

    // Provide users for the dialog
    dialogCmp.users = this.project.projectMemberPropertiesWrapper.map(
      (projectMemberWrapper) => projectMemberWrapper.member,
    );

    // Handle result
    this.subs.sink = ref.result
      .pipe(
        take(1),
        filter((result) => result !== UserOperation.Cancel),
      )
      .subscribe((result: ProjectTaskGridRow) => {
        this.upsertTask(result);
        this.makeApplyResult(result)(result.expenditureRateHistory);
      });
  }

  private upsertTask(taskGridRow: ProjectTaskGridRow): void {
    const idx = this.gridData.findIndex((row) => row.task.id === taskGridRow.task.id);

    if (idx < 0) {
      this.gridData.push(taskGridRow);
      return;
    }

    const updated: ProjectTaskGridRow = {
      ...taskGridRow,
    };

    this.gridData.splice(idx, 1, updated);
  }

  private getActiveColumnValue(row: ProjectTaskGridRow): [number, number] {
    let expenditureRate = 0;
    let budget = 0;

    if (this.showHourlyExpenditureRateFormField()) {
      const hist = row.expenditureRateHistory ?? [];
      expenditureRate = hist[hist.length - 1]?.rate ?? 0;
    }
    if (this.showBudgetMonetaryPerTaskFormField()) {
      budget = row.budgetMonetary ?? 0;
    }
    if (this.showBudgetHoursPerTaskFormField()) {
      budget = row.budgetSeconds ?? 0;
    }

    return [expenditureRate, budget];
  }

  private initContextMenuCallbacks(): void {
    this.gridWrapperComponent.contextMenuItems = [
      {
        text: 'Bearbeiten',
        faIcon: faPen,
        callback: (row: ProjectTaskGridRow) => this.openTaskDialog(UserOperation.Update, row),
        isVisible: true,
        isActivated: true,
      },
      {
        text: 'Entfernen',
        faIcon: faCircleMinus,
        callback: (row: ProjectTaskGridRow) => this.projectTaskGridService.removeTaskGridRow(row),
        isVisible: true,
        isActivated: true,
      },
    ];
  }
}
